import type { DataTableCell, DataTableColumn, DataTableColumnGroup, DataTableRow, DataTableRowLimit } from '@ui/chart/DataTable';
import type { BarChartSeries } from '@ui/chart/BarChart';
import { formatValue } from '@ui/chart/chartFormat';
import { ChannelEvolution, ChannelFigures, ChannelGroup, ChannelPart, ChannelReport, RevenueScope, SITUATION_DAYS, SituationDays, VatBasis } from '@app/model/bi/Bi';
import { occupiedOf, revenueOf } from '../Bi.utilities';
import { add, change, EVOLUTION_LINES, EvolutionMeasure, EvolutionValues, OTHER_LINE, ROW_LIMIT } from './RatePlans.utilities';

// The figures of the Channels report (BI report 5, ROO2-285). The formulas are those of the Channels Feature Specification
// v1.3 (chapter 6) with the grill decisions of 2026-10-23: every figure of a channel, a group or the total is a sum of its raw
// names, and every ratio comes from those sums (M-03, M-04), never from an average of rows. The report sends raw names;
// the page puts them into channels: D2 (the key: lower case, no whitespace) and the Channel Groups of the company (D1).

export const NO_CHANNEL = 'No channel';

// The column blocks of the table (D7: the same block ids in every BI report), in the order of the table. TODAY always shows.
export type ChannelBlock = 'build' | 'ly' | 'd1' | 'd3' | 'd7' | 'd14' | 'd30' | 'pu1' | 'pu3' | 'pu7' | 'pu14' | 'pu30';
export const CHANNEL_BLOCKS: { value: ChannelBlock, label: string }[] = [
	{ value: 'build', label: 'Build-up' },
	{ value: 'ly', label: 'Previous year (STLY, LY actual, Δ%)' },
	{ value: 'd1', label: 'Situation D-1' }, { value: 'd3', label: 'Situation D-3' }, { value: 'd7', label: 'Situation D-7' },
	{ value: 'd14', label: 'Situation D-14' }, { value: 'd30', label: 'Situation D-30' },
	{ value: 'pu1', label: 'PU 1' }, { value: 'pu3', label: 'PU 3' }, { value: 'pu7', label: 'PU 7' }, { value: 'pu14', label: 'PU 14' }, { value: 'pu30', label: 'PU 30' },
];
export const CHANNEL_BLOCK_IDS: ChannelBlock[] = CHANNEL_BLOCKS.map(b => b.value);
// D7: TODAY (always), BUILD-UP, PREVIOUS YEAR and PU 7.
export const DEFAULT_CHANNEL_BLOCKS: ChannelBlock[] = ['build', 'ly', 'pu7'];
const DAYS: Exclude<SituationDays, 0>[] = [1, 3, 7, 14, 30];
const ZERO: ChannelFigures = { roomNights: 0, occupiedRooms: 0, roomRevenue: 0, roomRevenueNet: 0, totalRevenue: 0, totalRevenueNet: 0 };
const cents = (v: number) => Math.round(v * 100) / 100;
const ratio = (a: number | null, b: number | null): number | null => a === null || b === null || b === 0 ? null : a / b;

// The sums of a row: today and the situations D-1 … D-30 (SITUATION_DAYS), STLY, LY actual, and today's figures of the
// properties that have stays in the last-year period (the base of Δ%, like-for-like).
export type ChannelSums = { cuts: ChannelFigures[], stly: ChannelFigures, ly: ChannelFigures, comparable: ChannelFigures };
const emptySums = (): ChannelSums => ({ cuts: SITUATION_DAYS.map(() => ZERO), stly: ZERO, ly: ZERO, comparable: ZERO });
const addPart = (s: ChannelSums, p: ChannelPart, missing: Set<number>): ChannelSums => ({
	cuts: SITUATION_DAYS.map((d, i) => add(s.cuts[i], p.situations.find(x => x.daysBefore === d) ?? ZERO)),
	stly: add(s.stly, p.stly), ly: add(s.ly, p.lyActual),
	comparable: missing.has(p.companyHotelId) ? s.comparable : add(s.comparable, p.situations.find(x => x.daysBefore === 0) ?? ZERO),
});
const sumOf = (parts: ChannelPart[], missing: Set<number>): ChannelSums => parts.reduce((s, p) => addPart(s, p, missing), emptySums());
const todayOf = (p: ChannelPart): number => p.situations.find(x => x.daysBefore === 0)?.roomNights ?? 0;

export type ChannelNode = {
	id: string,
	kind: 'GROUP' | 'CHANNEL' | 'NAME',
	title: string,
	// A raw name with a space at its end (D2 joins it with the name without the space): the row says so.
	note?: string,
	muted: boolean,
	// The channel keys of the row: one, or the keys of a Channel Group.
	keys: string[],
	// ROO2-284: the raw name of a NAME row, as the PMS writes it.
	name?: string,
	sums: ChannelSums,
	// Q7: the raw names of a channel with more than one name, or of a group.
	children: ChannelNode[],
};

export type ChannelTree = {
	// The channels, REV of today largest first (RQ-06): the groups and the keys in no group.
	rows: ChannelNode[],
	total: ChannelSums,
};

// The spelling of a key: the raw name with the most sold nights today in the scope; on a tie, the first in alphabetical order.
const titleOf = (parts: ChannelPart[]): string => {
	const byName = new Map<string, number>();
	parts.forEach(p => byName.set(p.name, (byName.get(p.name) ?? 0) + todayOf(p)));
	const best = [...byName.entries()].sort((a, b) => b[1] !== a[1] ? b[1] - a[1] : a[0].localeCompare(b[0]))[0][0];
	return best.trim() === '' ? NO_CHANNEL : best.trim();
};

const byRevenue = (scope: RevenueScope, vat: VatBasis) => (a: ChannelNode, b: ChannelNode): number => {
	const diff = (revenueOf(b.sums.cuts[0], scope, vat) ?? 0) - (revenueOf(a.sums.cuts[0], scope, vat) ?? 0);
	return diff !== 0 ? diff : a.title.localeCompare(b.title);
};

// The table tree of RQ-06 and RQ-10: a Channel Group or a key in no group is a row; a row with more than one raw name opens to
// its raw names (Q7). RQ-04: a group applies to every date, this year, STLY and LY actual alike, so a total never changes.
export const buildTree = (report: ChannelReport, groups: ChannelGroup[], scope: RevenueScope, vat: VatBasis): ChannelTree => {
	const missing = new Set(report.lastYearMissing);
	const order = byRevenue(scope, vat);
	const groupOf = new Map<string, ChannelGroup>();
	groups.forEach(g => g.keys.forEach(k => groupOf.set(k, g)));
	const rows = new Map<string, { group: ChannelGroup | null, parts: ChannelPart[] }>();
	report.parts.forEach(p => {
		const group = groupOf.get(p.key) ?? null;
		const id = group === null ? `key:${p.key}` : `group:${group.id}`;
		const row = rows.get(id) ?? { group, parts: [] };
		row.parts.push(p);
		rows.set(id, row);
	});
	const nameNodes = (id: string, parts: ChannelPart[]): ChannelNode[] => {
		const names = new Map<string, ChannelPart[]>();
		parts.forEach(p => names.set(p.name, [...(names.get(p.name) ?? []), p]));
		return [...names.entries()].map(([name, ps]): ChannelNode => ({
			id: `${id}|${name}`, kind: 'NAME', title: name.trim() === '' ? NO_CHANNEL : name.trim(),
			note: name !== name.trimEnd() && name.trim() !== '' ? '· a space at the end' : undefined,
			muted: name.trim() === '', keys: [ps[0].key], name, sums: sumOf(ps, missing), children: [],
		})).sort(order);
	};
	const out = [...rows.entries()].map(([id, { group, parts }]): ChannelNode => {
		const children = nameNodes(id, parts);
		const keys = [...new Set(parts.map(p => p.key))];
		return {
			id, kind: group === null ? 'CHANNEL' : 'GROUP', title: group?.title ?? titleOf(parts), muted: group === null && keys[0] === '',
			keys, sums: sumOf(parts, missing), children: children.length > 1 || group !== null ? children : [],
		};
	}).sort(order);
	return { rows: out, total: sumOf(report.parts, missing) };
};

// --- the table (RQ-06 … RQ-12) -------------------------------------------------------------------------------------------

// The cells of a row (RQ-10). shareOf: the revenue that SHARE divides by (the total). build: the BUILD-UP line of the row (M-12).
export const cellsOf = (node: Pick<ChannelNode, 'sums'>, title: string, shareOf: number | null, scope: RevenueScope, vat: VatBasis,
	build: (number | null)[] | null = null): Record<string, DataTableCell> => {
	const s = node.sums;
	const rev = (f: ChannelFigures) => revenueOf(f, scope, vat);
	const now = s.cuts[0];
	const adrNow = occupiedOf(now) === 0 ? null : ratio(rev(now), occupiedOf(now));
	const cells: Record<string, DataTableCell> = {
		name: title, rn: now.roomNights, rev: rev(now), adr: adrNow, share: ratio(rev(now), shareOf), build,
		lflRn: s.comparable.roomNights, lflRev: rev(s.comparable),
		stlyRn: s.stly.roomNights, stlyRev: rev(s.stly), vsStly: change(rev(s.comparable), rev(s.stly)),
		lyRn: s.ly.roomNights, lyRev: rev(s.ly), vsLy: change(rev(s.comparable), rev(s.ly)),
	};
	DAYS.forEach(d => {
		const past = s.cuts[SITUATION_DAYS.indexOf(d)];
		const pastRev = rev(past), nowRev = rev(now);
		const adrPast = occupiedOf(past) === 0 ? null : ratio(pastRev, occupiedOf(past));
		cells[`d${d}Rn`] = past.roomNights;
		cells[`d${d}Rev`] = pastRev;
		cells[`d${d}Adr`] = adrPast;
		cells[`pu${d}Rn`] = now.roomNights - past.roomNights;
		cells[`pu${d}Rev`] = nowRev === null || pastRev === null ? null : cents(nowRev - pastRev);
		// D4: ADR Δ (ROO2-271) = ADR today − ADR at situation D-n, as the other BI reports; not PU ADR (spec M-10).
		cells[`pu${d}Adr`] = adrNow === null || adrPast === null ? null : cents(adrNow - adrPast);
	});
	return cells;
};

// Q8: the meaning of each column (RQ-10, the last line), in the ⓘ after its label.
const info = (title: string, content: string) => ({ title, content });
const MEANING: Record<string, ReturnType<typeof info>> = {
	rn: info('RN', 'Room nights on the books for the period, now.'),
	rev: info('REV', 'Revenue on the books for the period, now, in the revenue of the switches.'),
	adr: info('ADR', 'REV ÷ RN, now.'),
	share: info('SHARE', 'The share of the channel in the revenue of the period, now.'),
	lflRn: info('TODAY LFL RN', 'Room nights now, without the properties that have no stays in the last-year period.'),
	lflRev: info('TODAY LFL REV', 'Revenue now, without the properties that have no stays in the last-year period. Δ% compares it with last year.'),
	stlyRn: info('STLY RN', 'Room nights for the same period last year, as they stood on the same as-of date last year.'),
	stlyRev: info('STLY REV', 'Revenue for the same period last year, as it stood on the same as-of date last year.'),
	vsStly: info('Δ% STLY', 'REV now against STLY REV. "new" when last year had no revenue.'),
	lyRn: info('LY ACTUAL RN', 'Room nights for the same period last year, as they stand today.'),
	lyRev: info('LY ACTUAL REV', 'Revenue for the same period last year, as it stands today.'),
	vsLy: info('Δ% LY ACTUAL', 'REV now against LY ACTUAL REV. "new" when last year had no revenue.'),
};
const situationInfo = (d: number, what: 'RN' | 'REV' | 'ADR') => info(`D-${d} ${what}`,
	`${what === 'RN' ? 'Room nights' : (what === 'REV' ? 'Revenue' : 'REV ÷ RN')} as it stood ${d} ${d === 1 ? 'day' : 'days'} before the as-of date.`);
const pickupInfo = (d: number, what: 'RN' | 'REV' | 'ADR Δ') => info(`PU ${d} ${what}`, what === 'RN'
	? `Room nights gained in the last ${d} ${d === 1 ? 'day' : 'days'}: RN now − RN at D-${d}. Cancellations reduce it.`
	: (what === 'REV' ? `Revenue gained in the last ${d} ${d === 1 ? 'day' : 'days'}: REV now − REV at D-${d}.`
		: `ADR now − ADR at D-${d}. No value when one of the two has no room nights.`));

// The columns and their header blocks (RQ-10), in the order of the spec. buildUp: the label of the BUILD-UP block ("Build-up
// 30D") and of its column (RN or REV). lfl: the scope has a property with no stays in the last-year period.
export const columnsOf = (blocks: ChannelBlock[], buildUp: { block: string, label: string } | null, lfl: boolean)
	: { columns: DataTableColumn[], groups: DataTableColumnGroup[] } => {
	// ROO2-284 (Greg 2026-10-25, Q2): RN and REV of today, of each situation D-n, of STLY, of LY actual and of each PU open the
	// bookings behind the number (clickable); ADR, SHARE, Δ%, the like-for-like columns and the build-up do not.
	const columns: DataTableColumn[] = [{ id: 'name', label: 'Channel' },
		{ id: 'rn', label: 'RN', format: 'number', info: MEANING.rn, clickable: true }, { id: 'rev', label: 'REV', format: 'money', info: MEANING.rev, clickable: true },
		{ id: 'adr', label: 'ADR', format: 'money', decimals: 2, info: MEANING.adr },
		{ id: 'share', label: 'SHARE', format: 'percent', decimals: 1, info: MEANING.share }];
	const groups: DataTableColumnGroup[] = [{ label: '', span: 1 }, { label: 'Today', span: 4 }];
	if (buildUp !== null && blocks.includes('build')) {
		columns.push({ id: 'build', label: buildUp.label, format: 'sparkline', valueFormat: buildUp.label === 'REV' ? 'money' : 'number',
			info: info(`Build-up ${buildUp.label}`, `The ${buildUp.label === 'REV' ? 'revenue' : 'room nights'} on the books for the period on each day of the window of the chart below. Red when the value fell.`) });
		groups.push({ label: buildUp.block, span: 1 });
	}
	if (blocks.includes('ly')) {
		if (lfl) columns.push({ id: 'lflRn', label: 'TODAY LFL RN', format: 'number', info: MEANING.lflRn }, { id: 'lflRev', label: 'TODAY LFL REV', format: 'money', info: MEANING.lflRev });
		columns.push({ id: 'stlyRn', label: 'STLY RN', format: 'number', info: MEANING.stlyRn, clickable: true },
			{ id: 'stlyRev', label: 'STLY REV', format: 'money', info: MEANING.stlyRev, clickable: true },
			{ id: 'vsStly', label: 'Δ%', format: 'percent', decimals: 1, signed: true, tone: 'change', info: MEANING.vsStly },
			{ id: 'lyRn', label: 'LY ACTUAL RN', format: 'number', info: MEANING.lyRn, clickable: true },
			{ id: 'lyRev', label: 'LY ACTUAL REV', format: 'money', info: MEANING.lyRev, clickable: true },
			{ id: 'vsLy', label: 'Δ%', format: 'percent', decimals: 1, signed: true, tone: 'change', info: MEANING.vsLy });
		groups.push({ label: lfl ? 'Previous year · like-for-like' : 'Previous year', span: lfl ? 8 : 6 });
	}
	DAYS.filter(d => blocks.includes(`d${d}` as ChannelBlock)).forEach(d => {
		columns.push({ id: `d${d}Rn`, label: 'RN', format: 'number', info: situationInfo(d, 'RN'), clickable: true },
			{ id: `d${d}Rev`, label: 'REV', format: 'money', info: situationInfo(d, 'REV'), clickable: true },
			{ id: `d${d}Adr`, label: 'ADR', format: 'money', decimals: 2, info: situationInfo(d, 'ADR') });
		groups.push({ label: `Situation D-${d}`, span: 3 });
	});
	// D8: a PU of 0 shows 0 in gray (the tone 'change'), not a blank cell.
	DAYS.filter(d => blocks.includes(`pu${d}` as ChannelBlock)).forEach(d => {
		columns.push({ id: `pu${d}Rn`, label: 'RN', format: 'number', signed: true, tone: 'change', info: pickupInfo(d, 'RN'), clickable: true },
			{ id: `pu${d}Rev`, label: 'REV', format: 'money', signed: true, tone: 'change', info: pickupInfo(d, 'REV'), clickable: true },
			{ id: `pu${d}Adr`, label: 'ADR Δ', format: 'money', decimals: 2, signed: true, tone: 'change', info: pickupInfo(d, 'ADR Δ') });
		groups.push({ label: `PU ${d}`, span: 3 });
	});
	return { columns, groups };
};

// RQ-07 and Q6: the first 15 channels in the current sort; a group lists its first 15 raw names.
export const ROW_LIMITS: DataTableRowLimit = {
	count: ROW_LIMIT,
	moreLabel: (hidden, _all, level) => level === 0 ? `+ ${hidden} more ${hidden === 1 ? 'channel' : 'channels'}, included in the total`
		: `+ ${hidden} more raw names, included in the channel's row`,
	showAllLabel: (all, level) => level === 0 ? `Show all ${all} channels` : `Show all ${all} raw names`,
	showTopLabel: `Show the top ${ROW_LIMIT}`,
};

// The DataTable rows of the tree. A row with raw names shows their count (Q7). buildOf gives the BUILD-UP line of a row.
export const tableRows = (tree: ChannelTree, scope: RevenueScope, vat: VatBasis,
	buildOf: (node: ChannelNode) => (number | null)[] | null = () => null): DataTableRow[] => {
	const totalRev = revenueOf(tree.total.cuts[0], scope, vat);
	const row = (n: ChannelNode): DataTableRow => ({
		id: n.id, muted: n.muted, note: n.children.length > 0 ? `· ${n.children.length}` : n.note,
		cells: cellsOf(n, n.title, totalRev, scope, vat, buildOf(n)),
		...(n.children.length > 0 ? { children: n.children.map(row) } : {}),
	});
	return tree.rows.map(row);
};

// The Total row; build: the BUILD-UP line of all channels (spec 7.2 shows it on Total too).
export const totalRow = (tree: ChannelTree, scope: RevenueScope, vat: VatBasis, build: (number | null)[] | null = null): DataTableRow =>
	({ id: 'total', cells: cellsOf({ sums: tree.total }, `Total (${tree.rows.length})`, revenueOf(tree.total.cuts[0], scope, vat), scope, vat, build) });

// --- the mix chart (RQ-17 … RQ-19) -----------------------------------------------------------------------------------------

export type MixMeasure = 'rev' | 'rn' | 'adr';
export type MixComparison = 'stly' | 'ly';
export const MIX_ROWS = 15;

// One row per channel in the order of the table (rows: the top rows in that order), the first 15, then "Other" (RQ-17); two
// bars: this period and the comparison. Shares from the sums of the total (M-04); ADR as a value (RQ-18).
export const mixOf = (rows: ChannelNode[], total: ChannelSums, measure: MixMeasure, comparison: MixComparison, scope: RevenueScope, vat: VatBasis) => {
	const top = rows.slice(0, MIX_ROWS), rest = rows.slice(MIX_ROWS);
	const rev = (f: ChannelFigures) => revenueOf(f, scope, vat);
	const sumRest = (pick: (s: ChannelSums) => ChannelFigures) => rest.reduce((f, n) => add(f, pick(n.sums)), ZERO);
	type Item = { title: string, now: ChannelFigures, last: ChannelFigures, comparable: ChannelFigures };
	const items: Item[] = [...top.map(n => ({ title: n.title, now: n.sums.cuts[0], last: comparison === 'stly' ? n.sums.stly : n.sums.ly, comparable: n.sums.comparable })),
		...(rest.length > 0 ? [{ title: `Other (${rest.length})`, now: sumRest(s => s.cuts[0]), last: sumRest(s => comparison === 'stly' ? s.stly : s.ly), comparable: sumRest(s => s.comparable) }] : [])];
	const totalNow = total.cuts[0], totalLast = comparison === 'stly' ? total.stly : total.ly;
	const value = (f: ChannelFigures, all: ChannelFigures) => measure === 'rev' ? ratio(rev(f), rev(all))
		: (measure === 'rn' ? ratio(f.roomNights, all.roomNights) : (occupiedOf(f) === 0 ? null : ratio(rev(f), occupiedOf(f))));
	const word = comparison === 'stly' ? 'STLY' : 'LY actual';
	const series: BarChartSeries[] = [
		{ id: 'now', label: 'This period', role: 'own', values: items.map(i => value(i.now, totalNow)) },
		{ id: 'last', label: word, role: 'compset', values: items.map(i => value(i.last, totalLast)) },
	];
	const money = (v: number | null) => v === null ? '–' : formatValue(v, 'money');
	const percent = (v: number | null) => v === null ? '–' : formatValue(v, 'percent', { decimals: 1 });
	// RQ-19: both values, both shares and the Δ% (M-07: revenue, like-for-like).
	const tooltipLines = (i: number) => {
		const it = items[i];
		if (it === undefined) return [];
		const delta = change(rev(it.comparable), rev(it.last));
		const deltaText = delta === null ? 'n/a' : (delta === 'new' ? 'new' : `${delta > 0 ? '+' : (delta < 0 ? '−' : '')}${formatValue(Math.abs(delta), 'percent', { decimals: 1 })}`);
		return [
			{ label: 'Revenue this period', value: money(rev(it.now)) },
			{ label: `Revenue ${word}`, value: money(rev(it.last)) },
			...(measure === 'rev' ? [] : [
				{ label: 'Share of revenue this period', value: percent(ratio(rev(it.now), rev(totalNow))) },
				{ label: `Share of revenue ${word}`, value: percent(ratio(rev(it.last), rev(totalLast))) },
			]),
			{ label: `Δ% of the revenue vs ${word}`, value: deltaText },
		];
	};
	return { categories: items.map(i => i.title), series, tooltipLines, rowIds: top.map(n => n.id) };
};

// --- the build-up (RQ-13 … RQ-16) ---------------------------------------------------------------------------------------

const zeroValues = (days: number): EvolutionValues => ({
	rn: Array.from({ length: days }, () => 0), occ: Array.from({ length: days }, () => 0), rev: Array.from({ length: days }, () => 0),
});
const plusValues = (a: EvolutionValues, b: EvolutionValues): EvolutionValues => ({
	rn: a.rn.map((v, i) => v + b.rn[i]),
	occ: a.occ.map((v, i) => v + b.occ[i]),
	// A sum of revenues has no value when one part has none (ROO2-236 R2).
	rev: a.rev.map((v, i) => { const w = b.rev[i]; return v === null || w === null ? null : cents(v + w); }),
});
export type ChannelLine = { id: string, title: string, thisYear: EvolutionValues, lastYear: EvolutionValues };

// M-11 for each row of the table: the sum of the raw names of its keys.
export const evolutionByRow = (evolution: ChannelEvolution, tree: ChannelTree): Map<string, ChannelLine> => {
	const n = evolution.days.length;
	const out = new Map<string, ChannelLine>();
	const lineOf = (keys: string[], names: string[] | null) => evolution.series
		.filter(s => keys.includes(s.key) && (names === null || names.includes(s.name)))
		.reduce((l, s) => ({
			thisYear: plusValues(l.thisYear, { rn: s.thisYearRn, occ: s.thisYearOccupied ?? s.thisYearRn, rev: s.thisYearRev }),
			lastYear: plusValues(l.lastYear, { rn: s.lastYearRn, occ: s.lastYearOccupied ?? s.lastYearRn, rev: s.lastYearRev }),
		}),
			{ thisYear: zeroValues(n), lastYear: zeroValues(n) });
	out.set('total', { id: 'total', title: 'Total', ...lineOf([...new Set(evolution.series.map(x => x.key))], null) });
	tree.rows.forEach(r => {
		out.set(r.id, { id: r.id, title: r.title, ...lineOf(r.keys, null) });
		r.children.forEach(c => out.set(c.id, { id: c.id, title: c.title, ...lineOf(c.keys, [c.id.slice(r.id.length + 1)]) }));
	});
	return out;
};

// RQ-13: one line for each of the first 6 channels in the order of the table, and a dashed "Other (N)" for the rest.
export const evolutionLines = (evolution: ChannelEvolution, tree: ChannelTree, order: string[] | null): { lines: ChannelLine[], other: ChannelLine | null } => {
	const byRow = evolutionByRow(evolution, tree);
	const n = evolution.days.length;
	const ids = order ?? tree.rows.map(r => r.id);
	const rows = ids.map(id => byRow.get(id)).filter((l): l is ChannelLine => l !== undefined);
	const rest = rows.slice(EVOLUTION_LINES);
	const other = rest.length === 0 ? null : {
		id: OTHER_LINE, title: `Other (${rest.length})`,
		thisYear: rest.reduce((v, r) => plusValues(v, r.thisYear), zeroValues(n)),
		lastYear: rest.reduce((v, r) => plusValues(v, r.lastYear), zeroValues(n)),
	};
	return { lines: rows.slice(0, EVOLUTION_LINES), other };
};

export type { EvolutionMeasure };

// --- the export (B8, Q5) ----------------------------------------------------------------------------------------------

// The shown blocks of the table for the file, today first, in the order of the table (the server leaves BUILD-UP out).
export const exportBlocks = (blocks: ChannelBlock[]): string[] => ['today', ...CHANNEL_BLOCK_IDS.filter(b => blocks.includes(b))];

// The sort of the table for the file: a header click, or the order of the page without one (REV, largest first).
export const exportSort = (sort: { columnId: string, direction: 'asc' | 'desc' } | undefined): { sort: string, direction: 'ASC' | 'DESC' } =>
	sort === undefined ? { sort: 'rev', direction: 'DESC' } : { sort: sort.columnId, direction: sort.direction === 'asc' ? 'ASC' : 'DESC' };

export const MIX_MEASURE_INPUT = { rev: 'REVENUE_SHARE', rn: 'RN_SHARE', adr: 'ADR' } as const;
export const MIX_COMPARISON_INPUT = { stly: 'STLY', ly: 'LY_ACTUAL' } as const;
// View by of the page → the view of the file ("Current view" follows the switch, P8).
export const VIEW_INPUT = { GROUPS: 'GROUPS', SINGLE: 'CHANNELS' } as const;

// ROO2-284: the node of a table row id (a channel, a group or a raw name), to ask for the bookings behind its numbers.
export const nodeOf = (tree: ChannelTree, rowId: string): ChannelNode | null => {
	for (const row of tree.rows) {
		if (row.id === rowId) return row;
		const child = row.children.find(c => c.id === rowId);
		if (child !== undefined) return child;
	}
	return null;
};
